import type { UseFormReturn } from 'react-hook-form';

import dayjs from 'dayjs';
import { useMemo, useState } from 'react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useUser } from '~/api/user';
import { Progress } from '~/components';
import { Field, Radio, Select } from '~/components/Form';

import type { VatRegulationFormType } from './VatRegulation.types';

import { optionsStyle, progressStyle, progressWrapperStyle, wrapperStyle, yearFieldStyle } from './VatRegulation.css';

const vatExemptRevenueLimit = 25_000;
const vatRegulationTypes = ['vatExempt', 'vatNormal'] as const;

export const VatRegulation = ({ form }: { form: UseFormReturn<VatRegulationFormType> }) => {
  const { data: user } = useUser();

  const { control, setValue } = form;
  const { t } = useTranslation(['settings']);

  const vatExempt = useWatch({ control, name: 'vatExempt' });

  const yearOptions = useMemo(
    () =>
      Object.keys(user.totalFcExclVatByYear)
        .map((revenueYear) => +revenueYear)
        .sort((a, b) => b - a),
    [user.totalFcExclVatByYear],
  );

  const [year, setYear] = useState(() => (yearOptions.includes(dayjs().year()) ? dayjs().year() : (yearOptions[0] ?? dayjs().year())));

  const totalRevenue = user.totalFcExclVatByYear[year] ?? 0;

  return (
    <div className={wrapperStyle}>
      <Field.Root>
        <Radio.Group
          className={optionsStyle}
          onValueChange={(value) => setValue('vatExempt', value === 'vatExempt', { shouldDirty: true, shouldValidate: true })}
          value={vatExempt === null ? null : vatExempt ? 'vatExempt' : 'vatNormal'}
        >
          {vatRegulationTypes.map((type) => (
            <Radio.Root key={type} value={type}>
              <Radio.Label>{t(`settings:profile.vatRegulation.toggle.${type}`)}</Radio.Label>
              <Radio.Description>{t(`settings:profile.vatRegulation.options.${type}.description`)}</Radio.Description>
            </Radio.Root>
          ))}
        </Radio.Group>
      </Field.Root>

      {vatExempt && (
        <div className={progressWrapperStyle}>
          {yearOptions.length > 1 && (
            <Field.Root className={yearFieldStyle}>
              <Select.Root onValueChange={(value) => setYear(value ?? dayjs().year())} value={year}>
                <Select.Button aria-label={t('settings:profile.vatRegulation.yearSelector.ariaLabel')} />

                <Select.List>
                  {yearOptions.map((optionYear) => (
                    <Select.Item key={optionYear} value={optionYear}>
                      {optionYear}
                    </Select.Item>
                  ))}
                </Select.List>
              </Select.Root>
            </Field.Root>
          )}

          <Progress
            className={progressStyle}
            label={t('settings:profile.vatRegulation.limit', { year })}
            max={vatExemptRevenueLimit}
            type="currency"
            value={totalRevenue}
          />
        </div>
      )}
    </div>
  );
};
